/**
 * Wave 78 F1 · SUB 1 — Lazy Google Maps JS SDK loader for the Casa app.
 *
 * Ported (with intentional duplication) from apps/web/src/lib/google-maps-loader.ts.
 * The Casa bundle is deliberately standalone — no workspace cross-imports —
 * so we re-declare the loader here instead of pulling apps/web into the
 * Vite build graph. Same singleton-Promise contract: one script tag per
 * tab, idempotent calls return the cached resolution.
 *
 * Why a separate loader instead of a shared package:
 *   • apps/casa ships to a different Cloudflare Pages project than apps/web,
 *     with its own build, env, and bundle budget. Forcing a shared package
 *     means coupling builds.
 *   • The loader is 50 LOC and stable — duplication cost is low.
 *   • The Casa env can have a different referrer-restricted key (e.g.
 *     locked to *.nullscience.ai/*) without breaking the Builder admin one.
 *
 * Env: VITE_GOOGLE_MAPS_API_KEY. When missing, callers fall back to the
 * static-thumbnail style placeholder.
 */

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace google {
    namespace maps {
      class Map {
        constructor(el: Element, opts?: Record<string, unknown>);
        setMapTypeId(id: string): void;
        setCenter(p: { lat: number; lng: number }): void;
        setZoom(z: number): void;
        getStreetView(): { setVisible(v: boolean): void; setPosition(p: { lat: number; lng: number }): void };
      }
      class Marker {
        constructor(opts?: Record<string, unknown>);
        setMap(map: Map | null): void;
      }
      class LatLng {
        constructor(lat: number, lng: number);
        lat(): number;
        lng(): number;
      }
      const MapTypeId: { ROADMAP: string; SATELLITE: string; HYBRID: string; TERRAIN: string };
      const ControlPosition: {
        RIGHT_TOP: number;
        RIGHT_BOTTOM: number;
        LEFT_TOP: number;
        LEFT_BOTTOM: number;
        TOP_RIGHT: number;
        TOP_LEFT: number;
        BOTTOM_LEFT: number;
        BOTTOM_RIGHT: number;
      };
      namespace event {
        function clearInstanceListeners(instance: unknown): void;
      }
    }
  }
}

let loaderPromise: Promise<typeof google.maps> | null = null;

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (loaderPromise) return loaderPromise;

  const key = (import.meta as unknown as {
    env?: { VITE_GOOGLE_MAPS_API_KEY?: string };
  }).env?.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(
      new Error(
        'VITE_GOOGLE_MAPS_API_KEY missing — Casa bundle cannot load Maps JS SDK. ' +
          'Set it in apps/casa/.env.production and rebuild.',
      ),
    );
  }

  loaderPromise = new Promise<typeof google.maps>((resolve, reject) => {
    if (
      typeof window !== 'undefined' &&
      (window as { google?: { maps?: typeof google.maps } }).google?.maps
    ) {
      resolve((window as unknown as { google: { maps: typeof google.maps } }).google.maps);
      return;
    }

    const cbName = `__gmaps_casa_cb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    (window as unknown as Record<string, () => void>)[cbName] = () => {
      try {
        const maps = (window as unknown as { google: { maps: typeof google.maps } }).google.maps;
        resolve(maps);
      } catch (e) {
        reject(e);
      } finally {
        delete (window as unknown as Record<string, () => void>)[cbName];
      }
    };

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(key)}` +
      `&callback=${cbName}` +
      `&language=pt-BR&region=BR&loading=async`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loaderPromise = null;
      delete (window as unknown as Record<string, () => void>)[cbName];
      reject(
        new Error(
          'Failed to load Google Maps SDK. ' +
            'Check VITE_GOOGLE_MAPS_API_KEY referrer restrictions in Google Cloud Console.',
        ),
      );
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

/** Test-only — reset the cached promise between mount cycles. */
export function __resetGoogleMapsLoaderForTests(): void {
  loaderPromise = null;
}
